import { useEffect, useState, useMemo } from "react";
import { vceFirestore, hvoFirestore } from "configs/FirebaseConfig";
import { FIRESTORE_ORDERS_TABLE } from "constants/FirestoreConstant";

/**
 * Hook to fetch and calculate campaign performance metrics
 * @param {string} campaignId - Campaign ID
 * @param {string} shopSource - Shop source (VCE or HVO)
 * @param {object} options - Additional options (dateRange, etc.)
 */
export const useCampaignPerformance = (campaignId, shopSource, options = {}) => {
  const { startDate = null, endDate = null } = options;
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!campaignId || !shopSource) {
      setIsLoading(false);
      return;
    }

    const fetchCampaignOrders = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const firestore = shopSource === "VCE" ? vceFirestore : hvoFirestore;
        
        // Query orders that have this campaign in their attribution
        const ordersSnapshot = await firestore
          .collection(FIRESTORE_ORDERS_TABLE)
          .where("campaignAttribution", "!=", null)
          .get();

        // Filter orders that include this specific campaign
        const campaignOrders = ordersSnapshot.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
          .filter((order) => {
            // Check if this campaign is in the attribution array
            const hasThisCampaign = order.campaignAttribution?.some(
              (attr) => attr.campaignId === campaignId
            );

            if (!hasThisCampaign) return false;

            // Apply date range filter if provided
            if (startDate || endDate) {
              const orderDate = order.createdAt;
              if (startDate && orderDate < startDate) return false;
              if (endDate && orderDate > endDate) return false;
            }

            return true;
          });

        setOrders(campaignOrders);
      } catch (err) {
        console.error("Error fetching campaign orders:", err);
        setError(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCampaignOrders();
  }, [campaignId, shopSource, startDate, endDate]);

  // Calculate metrics
  const metrics = useMemo(() => {
    if (orders.length === 0) {
      return {
        totalOrders: 0,
        totalRevenue: 0,
        averageOrderValue: 0,
        newCustomers: 0,
        returningCustomers: 0,
        newCustomerRate: 0,
        totalProducts: 0,
        uniqueCustomers: 0,
      };
    }

    const totalOrders = orders.length;
    const totalRevenue = orders.reduce((sum, order) => sum + (order.totalPrice || 0), 0);
    const averageOrderValue = totalRevenue / totalOrders;

    // Track unique customers
    const customerIds = new Set();
    const firstTimeCustomers = new Set();

    orders.forEach((order) => {
      if (order.customer?.uid && order.customer.uid !== "0") {
        customerIds.add(order.customer.uid);
        
        // Check if this was their first order (simplified - would need order history check)
        // For now, we'll use a heuristic based on campaign attribution
        const isFirstOrder = order.campaignAttribution?.some(
          (attr) => attr.campaignGoal === "acquisition"
        );
        if (isFirstOrder) {
          firstTimeCustomers.add(order.customer.uid);
        }
      }
    });

    const uniqueCustomers = customerIds.size;
    const newCustomers = firstTimeCustomers.size;
    const returningCustomers = uniqueCustomers - newCustomers;
    const newCustomerRate = uniqueCustomers > 0 ? (newCustomers / uniqueCustomers) * 100 : 0;

    // Count total products sold
    const totalProducts = orders.reduce((sum, order) => {
      return sum + (order.products?.reduce((pSum, p) => pSum + (p.quantity || 0), 0) || 0);
    }, 0);

    return {
      totalOrders,
      totalRevenue,
      averageOrderValue,
      newCustomers,
      returningCustomers,
      newCustomerRate,
      totalProducts,
      uniqueCustomers,
    };
  }, [orders]);

  // Product analysis
  const productAnalysis = useMemo(() => {
    const productMap = new Map();

    orders.forEach((order) => {
      order.products?.forEach((product) => {
        const key = product.productId;
        const existing = productMap.get(key) || {
          productId: product.productId,
          name: product.name,
          quantity: 0,
          revenue: 0,
          orders: 0,
        };

        existing.quantity += product.quantity || 0;
        existing.revenue += product.paymentPrice * product.quantity || 0;
        existing.orders += 1;

        productMap.set(key, existing);
      });
    });

    const products = Array.from(productMap.values());
    
    // Sort by revenue
    products.sort((a, b) => b.revenue - a.revenue);

    return {
      topProducts: products.slice(0, 10),
      totalUniqueProducts: products.length,
      allProducts: products,
    };
  }, [orders]);

  // Cross-sell analysis
  const crossSellAnalysis = useMemo(() => {
    const productPairs = new Map();

    orders.forEach((order) => {
      const products = order.products || [];
      
      // Create pairs of products bought together
      for (let i = 0; i < products.length; i++) {
        for (let j = i + 1; j < products.length; j++) {
          const product1 = products[i];
          const product2 = products[j];
          
          // Create a consistent key (alphabetically sorted)
          const key = [product1.productId, product2.productId].sort().join("_");
          
          const existing = productPairs.get(key) || {
            product1: { id: product1.productId, name: product1.name },
            product2: { id: product2.productId, name: product2.name },
            count: 0,
            revenue: 0,
          };

          existing.count += 1;
          existing.revenue += (product1.paymentPrice * product1.quantity) + 
                             (product2.paymentPrice * product2.quantity);

          productPairs.set(key, existing);
        }
      }
    });

    const pairs = Array.from(productPairs.values());
    pairs.sort((a, b) => b.count - a.count);

    return {
      topPairs: pairs.slice(0, 10),
      totalPairs: pairs.length,
    };
  }, [orders]);

  // Attribution method breakdown
  const attributionBreakdown = useMemo(() => {
    const methods = {
      utm: 0,
      discount_code: 0,
      product_targeting: 0,
      category_targeting: 0,
    };

    orders.forEach((order) => {
      const campaignAttr = order.campaignAttribution?.find(
        (attr) => attr.campaignId === campaignId
      );
      
      if (campaignAttr?.matchMethod) {
        methods[campaignAttr.matchMethod] = (methods[campaignAttr.matchMethod] || 0) + 1;
      }
    });

    return methods;
  }, [orders, campaignId]);

  // Time series data (daily)
  const timeSeriesData = useMemo(() => {
    const dailyData = new Map();

    orders.forEach((order) => {
      const date = new Date(order.createdAt * 1000);
      const dateKey = date.toISOString().split("T")[0]; // YYYY-MM-DD

      const existing = dailyData.get(dateKey) || {
        date: dateKey,
        orders: 0,
        revenue: 0,
        customers: new Set(),
      };

      existing.orders += 1;
      existing.revenue += order.totalPrice || 0;
      if (order.customer?.uid && order.customer.uid !== "0") {
        existing.customers.add(order.customer.uid);
      }

      dailyData.set(dateKey, existing);
    });

    // Convert to array and add customer count
    const series = Array.from(dailyData.values()).map((day) => ({
      date: day.date,
      orders: day.orders,
      revenue: day.revenue,
      customers: day.customers.size,
    }));

    // Sort by date
    series.sort((a, b) => a.date.localeCompare(b.date));

    return series;
  }, [orders]);

  return {
    orders,
    metrics,
    productAnalysis,
    crossSellAnalysis,
    attributionBreakdown,
    timeSeriesData,
    isLoading,
    error,
  };
};

/**
 * Hook to compare multiple campaigns
 */
export const useCampaignComparison = (campaignIds, shopSource) => {
  const [comparisons, setComparisons] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!campaignIds || campaignIds.length === 0) {
      setIsLoading(false);
      return;
    }

    const fetchComparisons = async () => {
      setIsLoading(true);

      try {
        const firestore = shopSource === "VCE" ? vceFirestore : hvoFirestore;
        
        const ordersSnapshot = await firestore
          .collection(FIRESTORE_ORDERS_TABLE)
          .where("campaignAttribution", "!=", null)
          .get();

        const campaignMetrics = new Map();

        ordersSnapshot.docs.forEach((doc) => {
          const order = doc.data();
          
          order.campaignAttribution?.forEach((attr) => {
            if (campaignIds.includes(attr.campaignId)) {
              const existing = campaignMetrics.get(attr.campaignId) || {
                campaignId: attr.campaignId,
                campaignName: attr.campaignName,
                orders: 0,
                revenue: 0,
              };

              existing.orders += 1;
              existing.revenue += order.totalPrice || 0;

              campaignMetrics.set(attr.campaignId, existing);
            }
          });
        });

        const results = Array.from(campaignMetrics.values());
        results.forEach((result) => {
          result.averageOrderValue = result.orders > 0 ? result.revenue / result.orders : 0;
        });

        setComparisons(results);
      } catch (error) {
        console.error("Error fetching campaign comparisons:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchComparisons();
  }, [campaignIds, shopSource]);

  return { comparisons, isLoading };
};
